import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../../../../../hooks/use-is-mounted";
import {useCachedIndustrySchemaController} from "../../../../../../../../../../hooks/use-industry";
import Api from "../../../../../../../../../../../core/services/api/api";
import Utils from "../../../../../../../../../../../core/services/utils/utils";

const UserHomepageControlPanelComponentSearchNexusSummary = ({searchToken, rerenderer}) => {
    const {getSchemaForIndustry} = useCachedIndustrySchemaController()
    const [searchData, setSearchData] = useState({});
    const [searchSchema, setSearchSchema] = useState({entries: []});
    const isMounted = useIsMounted();

    /**
     * Fetches the search data from the search token
     * @type {(function(): Promise<void>)}
     */
    const getSearchDataFromSearchToken = useCallback(async () => {
        const response = await Api.getNexusSearchLog(searchToken);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const orderBy = response.data?.filters?.find(e => e.isForSorting);
            setSearchData({
                industryId: response.data?.industryId,
                keyword: response.data?.keyword,
                filters: response.data?.filters?.filter(e => e.isForFiltering).map(e => ({
                    filterId: e.industryIndexStructureId,
                    value: e.value,
                })),
                orderBy: orderBy
                    ? {id: orderBy.industryIndexStructureId, isDescending: orderBy.isDesc}
                    : null,
            })
            if (response.data?.industryId) {
                setSearchSchema(await getSchemaForIndustry(response.data?.industryId) ?? {});
            }
        }
    }, [isMounted, searchToken, getSchemaForIndustry, rerenderer])


    /**
     * With each change searchToken:
     * - fetches the search data from the search token.
     */
    useEffect(() => {
        void getSearchDataFromSearchToken();
    }, [getSearchDataFromSearchToken])

    /**
     * Create the values used for basic search summary
     */
    const summary = useMemo(() => {
        const res = [];
        if (!searchData)
            return res;
        if (searchData.keyword) {
            res.push(
                <p key={'keyword'} className={'value line'}>
                    <span className={'title'}>
                        Keywords:
                    </span>
                    {searchData.keyword ?? ''}
                </p>
            )
        }
        if (searchData.orderBy) {
            const entry = searchSchema.entries.find(e => e.id === searchData.orderBy.id);
            res.push(
                <p key={'keyword'} className={'value line'}>
                    <span className={'title'}>
                        Order By:
                    </span>
                    {entry?.title ?? ''} (${searchData.orderBy.isDescending ? 'Descending' : 'Ascending'})
                </p>
            )
        }
        const groupedFilters = searchData.filters?.reduce((acc, filter) => {
            if (!acc[filter.filterId])
                acc[filter.filterId] = {filterId: filter.filterId, values: []};
            acc[filter.filterId].values.push(filter.value);
            return acc;
        }, {}) ?? []
        for (const filter of Object.values(groupedFilters)) {
            const entry = searchSchema.entries.find(e => e.id === filter.filterId);
            if (!entry)
                continue;
            res.push(
                <p key={filter.filterId} className={'value line'}>
                    <span className={'title'}>
                        {entry.title ?? ""}
                    </span>
                    {filter.values.map(e => Utils.formatTechnicalPropertyValueDisplay(entry?.dataType, e, entry.nullReplacement)).join(', ')}
                </p>
            )
        }

        return (
            <>
                {res}
            </>
        );
    }, [searchData, searchSchema]);


    return (
        <div className={'component-search-summary'}>
            {summary}
        </div>
    );

}

export default UserHomepageControlPanelComponentSearchNexusSummary;
